"use client"

import { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"

import { cn } from "../../utils/utils"
import Logo from "../../assests/Logo.jpg"
import { auth } from "../../firebase"
import { Drawer, DrawerContent } from "../../components/ui/drawer"
import {
  LogOutIcon,
  HistoryIcon,
  SettingsIcon,
  LightbulbIcon,
  FileBarChartIcon,
  XIcon,
  ChevronRightIcon,
  BanIcon,
  CandlestickChartIcon,
  UserCog2Icon,
  GitForkIcon,
  HandCoins,
  FolderClock,
  ShieldCheck,
  CircleAlertIcon,
} from "lucide-react"

const Icons = {
  home: <FileBarChartIcon className="mr-5 h-5 w-5" />,
  advice: <LightbulbIcon className="mr-5 h-5 w-5" />,
  history: <HistoryIcon className="mr-5 h-5 w-5" />,
  sub2: <SettingsIcon className="mr-[17px] h-[24px] w-[24px]" />,
  ignore: <BanIcon className="mr-5 h-5 w-5" />,
  userRound: <UserCog2Icon className="mr-5 h-5 w-5" />,
  order: <CandlestickChartIcon className="mr-5 h-5 w-5" />,
  strategy: <GitForkIcon className="mr-5 h-5 w-5 -rotate-90" />,
  pricing: <HandCoins className="mr-5 h-5 w-5 -rotate-90" />,
  previousOrders: <FolderClock className="mr-5  h-5 w-5 " />,
}

const mobileNavItems = [
  {
    title: "Live Portfolio",
    href: "/user",
    path: "/user",
    icon: "home",
  },
  {
    title: "Advice",
    href: "/stock-recommendation",
    path: "stock-recommendation",
    icon: "advice",
  },
  {
    title: "Order Details",
    href: "/order-details",
    path: "order-details",
    icon: "order",
  },
  {
    title: "Exited Trade History",
    href: "/trade-history",
    path: "history",
    icon: "history",
  },
  {
    title: "Broker Settings",
    href: "/subscriptions",
    path: "subscriptions",
    icon: "sub2",
  },
  {
    title: "Plans / Portfolios",
    href: "/pricing",
    path: "pricing",
    icon: "pricing",
  },
  {
    title: "My Invoices",
    href: "/previous-orders",
    path: "previous-orders",
    icon: "previousOrders",
    tooltip: "Previous Orders",
  },
  {
    title: "Contact RA",
    href: "/contact-advisor",
    path: "contact-advisor",
    icon: "userRound",
  },
]

const MobileNavBar = ({ isOpen, onClose, openUserProfileModal, userDetails, adviceTradeData, ignoreTradesData }) => {
  const path = window.location.pathname
  const whiteLabelText = process.env.REACT_APP_WHITE_LABEL_TEXT
  const [showLogoutModel, setShowLogoutModel] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const prevOpenRef = useRef(false)
  const isInitialMount = useRef(true)

  // Add animation styles to document
  useEffect(() => {
    const styleEl = document.createElement("style")
    styleEl.textContent = `
      @keyframes slideInFromLeft {
        0% {
          transform: translateX(-30px);
          opacity: 0;
        }
        100% {
          transform: translateX(0);
          opacity: 1;
        }
      }
      
      .animate-slideInFromLeft {
        animation: slideInFromLeft 0.4s ease-out forwards;
      }

      @keyframes slideInNav {
        0% {
          transform: translateX(-100%);
          opacity: 0.8;
          box-shadow: -5px 0 15px rgba(0, 0, 0, 0);
        }
        70% {
          transform: translateX(-5%);
          opacity: 1;
        }
        100% {
          transform: translateX(0);
          opacity: 1;
          box-shadow: -5px 0 15px rgba(0, 0, 0, 0.2);
        }
      }
      
      .animate-slideInNav {
        animation: slideInNav 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      }
      
      @keyframes slideOutNav {
        0% {
          transform: translateX(0);
          opacity: 1;
          box-shadow: -5px 0 15px rgba(0, 0, 0, 0.2);
        }
        100% {
          transform: translateX(-100%);
          opacity: 0.8;
          box-shadow: -5px 0 15px rgba(0, 0, 0, 0);
        }
      }
      
      .animate-slideOutNav {
        animation: slideOutNav 0.4s cubic-bezier(0.7, 0, 0.84, 0) forwards;
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      .animate-fadeIn {
        animation: fadeIn 0.3s ease-out forwards;
      }
    `
    document.head.appendChild(styleEl)

    return () => {
      document.head.removeChild(styleEl)
    }
  }, [])

  // Control animation state
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the drawer is visible first
      setTimeout(() => {
        setShouldAnimate(true)
      }, 100)
    } else {
      setShouldAnimate(false)
    }
  }, [isOpen])

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 768)
    checkIsMobile()
    window.addEventListener("resize", checkIsMobile)
    return () => window.removeEventListener("resize", checkIsMobile)
  }, [])

  var initials
  var fullName = userDetails && userDetails.name
  initials =
    fullName &&
    fullName
      .split(" ")
      .map((name) => name[0])
      .join("")
      .toUpperCase()

  // Calculate profile completion circle
  const circumference = 2 * Math.PI * 20 // Adjust radius as needed
  const profileCompletion =
    userDetails && typeof userDetails.profile_completion === "number" ? userDetails.profile_completion : 0
  const strokeDashoffset = circumference - (profileCompletion / 100) * circumference

  if (!mobileNavItems?.length) {
    return null
  }

  return (
    <div className={`fixed inset-0 z-50 ${isOpen ? "block" : "hidden"} transition-all duration-300 ease-in-out`}>
      {/* Background Overlay */}
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm ${
          isOpen ? "animate-fadeIn" : "opacity-0"
        } transition-opacity duration-300`}
        onClick={onClose}
      ></div>

      {/* Drawer */}
      <div
        className={`fixed inset-y-0 left-0 w-[85%] max-w-[320px] bg-[#0A0F1D] z-50 transform ${
          isOpen ? "translate-x-0 animate-slideInNav" : "-translate-x-full animate-slideOutNav"
        } shadow-xl`}
      >
        <aside className="flex flex-col h-full">
          {/* Close Button */}
          <div
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-1.5 rounded-full bg-[#1E3A8A]/20 hover:bg-[#1E3A8A]/30 transition-colors"
          >
            <XIcon className="h-5 w-5 text-white" />
          </div>

          {/* Header */}
          <div
            className={`p-5 border-b border-[#1E3A8A]/30 ${shouldAnimate ? "animate-slideInFromLeft" : ""}`}
            style={{
              animationDelay: "0.05s",
              animationFillMode: "both",
            }}
          >
            <div className="flex items-center space-x-3">
              <img src={Logo || "/placeholder.svg"} alt="Logo" className="w-10 h-10 rounded-md shadow-md" />
              <div className="text-xl font-semibold text-white ">{whiteLabelText}</div>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="flex-1 overflow-y-auto py-2">
            {mobileNavItems.map((item, index) => {
              const Icon = Icons[item.icon]
              const isActive = path === item.href || path.startsWith(`/${item.path}`)
              const animationDelay = `${0.1 * (index + 1)}s`

              return (
                item.href && (
                  <Link key={index} to={item.disabled ? "/" : item.href} onClick={onClose}>
                    <div
                      className={cn(
                        "flex items-center px-5 py-3 border-l-4 text-[15px] font-medium",
                        isActive
                          ? "bg-[#2056DF]/10 border-[#2056DF] text-white"
                          : "border-transparent text-gray-300 hover:bg-[#2056DF]/10 hover:text-white",
                        item.disabled && "opacity-50 cursor-not-allowed",
                      )}
                      style={{
                        opacity: shouldAnimate ? 1 : 0,
                        transform: shouldAnimate ? "translateX(0)" : "translateX(-20px)",
                        transition: `opacity 0.4s ease-out, transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)`,
                        transitionDelay: animationDelay,
                      }}
                    >
           <span className={isActive ? "text-[#2056DF]" : ""}>
      {Icon}
    </span>
                      <span>{item.title}</span>
                      {item.icon === "advice" && adviceTradeData && adviceTradeData.length >= 1 && (
                        <span className="ml-auto px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-600 text-white">
                          {adviceTradeData.length}
                        </span>
                      )}
                      {item.icon === "ignore" && ignoreTradesData && ignoreTradesData.length >= 1 && (
                        <span className="ml-auto text-blue-500 font-medium">({ignoreTradesData.length})</span>
                      )}
                    </div>
                  </Link>
                )
              )
            })}

            {/* Logout Button */}
            <div
              onClick={() => setShowLogoutModel(true)}
              className="flex items-center px-5 py-3 border-l-4 border-transparent text-[15px] font-medium text-gray-300 hover:bg-[#2056DF]/10 hover:text-white cursor-pointer"
              style={{
                opacity: shouldAnimate ? 1 : 0,
                transform: shouldAnimate ? "translateX(0)" : "translateX(-20px)",
                transition: `opacity 0.4s ease-out, transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)`,
                transitionDelay: `${0.1 * (mobileNavItems.length + 1)}s`,
              }}
            >
              <LogOutIcon className="h-5 w-5 mr-5" />
              <span>Logout</span>
            </div>
          </div>

          {/* Profile Section - Incomplete Profile */}
          {userDetails && userDetails.profile_completion !== 100 && (
            <div
              className="border-t border-[#333]/50 p-5 space-y-4"
              style={{
                opacity: shouldAnimate ? 1 : 0,
                transform: shouldAnimate ? "translateX(0)" : "translateX(-20px)",
                transition: `opacity 0.4s ease-out, transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)`,
                transitionDelay: `${0.1 * (mobileNavItems.length + 2)}s`,
              }}
            >
              <div className="flex items-center space-x-3">
                {userDetails?.image_url ? (
                  <div className="w-12 h-12 rounded-full ring-2 ring-[#2563EB]/70 overflow-hidden">
                    <img
                      src={userDetails.image_url || "/placeholder.svg"}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[#1E3A8A] text-white font-medium text-lg border-2 border-[#2563EB]/70">
                    {initials}
                  </div>
                )}
                <div>
                  <div className="text-white font-medium truncate max-w-[200px]">
                    {userDetails.name || userDetails?.email.split("@")[0]}
                  </div>
                  <div className="text-gray-400 text-sm truncate max-w-[200px]">{userDetails.email}</div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="flex-1 bg-[#333] h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-[#2056DF] h-full rounded-full"
                    style={{ width: `${userDetails.profile_completion}%` }}
                  ></div>
                </div>
                <div className="text-white font-medium text-sm">{userDetails.profile_completion}%</div>
              </div>

              <button
                onClick={openUserProfileModal}
                className="w-full flex items-center justify-center py-2.5 px-4 bg-[#2056DF] hover:bg-[#2056DF]/90 text-white font-medium rounded-md transition-colors"
              >
                Complete Profile
                <ChevronRightIcon className="h-5 w-5 ml-1" />
              </button>
            </div>
          )}

          {/* Profile Section - Complete Profile */}
          {userDetails && userDetails.profile_completion === 100 && (
            <div
              className="border-t border-[#333]/50 p-5"
              style={{
                opacity: shouldAnimate ? 1 : 0,
                transform: shouldAnimate ? "translateX(0)" : "translateX(-20px)",
                transition: `opacity 0.4s ease-out, transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)`,
                transitionDelay: `${0.1 * (mobileNavItems.length + 2)}s`,
              }}
            >
              <div className="flex items-center space-x-3">
                {userDetails?.image_url ? (
                  <div className="w-12 h-12 rounded-full ring-2 ring-[#2563EB] overflow-hidden">
                    <img
                      src={userDetails.image_url || "/placeholder.svg"}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[#1E3A8A] text-white font-medium text-lg border-2 border-[#2563EB]">
                    {initials}
                  </div>
                )}
                <div>
                  <div className="flex items-center">
                    <div className="text-white font-medium truncate max-w-[160px]">
                      {userDetails.name || userDetails?.email.split("@")[0]}
                    </div>
                    <ShieldCheck className="h-3.5 w-3.5 ml-1 text-[#2056DF] flex-shrink-0" />
                  </div>
                  <div className="text-gray-400 text-sm truncate max-w-[200px]">{userDetails.email}</div>
                </div>
              </div>
            </div>
          )}

          {/* Help Section */}
          <div
            className="border-t border-[#333]/50 p-4"
            style={{
              opacity: shouldAnimate ? 1 : 0,
              transform: shouldAnimate ? "translateX(0)" : "translateX(-20px)",
              transition: `opacity 0.4s ease-out, transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)`,
              transitionDelay: `${0.1 * (mobileNavItems.length + 3)}s`,
            }}
          >
            <div className="flex items-center text-gray-300 text-xs">
              <CircleAlertIcon className="text-[#2056DF] h-4 mr-1" />
              How to use {whiteLabelText}
              <Link
                onClick={onClose}
                to="/help"
                className="ml-2 text-[#2056DF] text-xs font-medium hover:text-[#1E40AF] transition-colors duration-200 hover:underline"
              >
                Click here
              </Link>
            </div>
          </div>
        </aside>
      </div>

      {/* Logout Modal */}
      {isMobile ? (
        <Drawer open={showLogoutModel} onOpenChange={setShowLogoutModel}>
          <DrawerContent className="focus:outline-none max-h-[240px] bg-black/70 rounded-t-xl">
            <div className="p-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-white">Are you sure you want to logout?</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setShowLogoutModel(false)}
                  className="py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => auth.signOut()}
                  className="py-2.5 px-4 bg-gradient-to-r from-[#FF0000] to-[#DC2626] hover:from-[#DC2626] hover:to-[#B91C1C] text-white font-medium rounded-md transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        showLogoutModel && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50 px-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="absolute top-4 right-4">
                  <button onClick={() => setShowLogoutModel(false)} className="text-gray-500 hover:text-gray-700">
                    <XIcon className="w-5 h-5" />
                  </button>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center mt-2">
                  Are you sure you want to logout?
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setShowLogoutModel(false)}
                    className="py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => auth.signOut()}
                    className="py-3 px-4 bg-[#2056DF] hover:bg-[#2056DF]/90 text-white font-medium rounded-md transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </div>
  )
}

export default MobileNavBar
